import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  X, RotateCcw, Maximize2, Pause, Play, Video, Move3d, ArrowDownUp, Orbit,
  Gauge, MapPin, Keyboard, Gamepad2, FlaskConical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getRunnerSrc, getRunnerKey } from "@/lib/runners";

const CAMERAS = [
  ["follow", "3rd Person", Move3d],
  ["top", "Top-down", ArrowDownUp],
  ["orbit", "Orbit", Orbit],
];

// Full-screen pre-publish playtest sandbox. Loads the same runner as the live
// game, plus a debug HUD, camera switcher and controller hints.
export default function TestPlay({ config, open, onClose }) {
  const iframeRef = useRef(null);
  const wrapRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [cam, setCam] = useState("follow");
  const [paused, setPaused] = useState(false);
  const [hud, setHud] = useState(null);
  const is3d = config?.dimension === "3d";
  const runnerSrc = getRunnerSrc(config?.dimension);
  const runnerKey = getRunnerKey(config?.dimension) + "-test";

  const send = useCallback((msg) => {
    iframeRef.current?.contentWindow?.postMessage(msg, "*");
  }, []);

  useEffect(() => {
    if (!open) { setReady(false); setHud(null); setPaused(false); setCam("follow"); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (ev) => {
      const d = ev.data || {};
      if (d.type === "ready") {
        setReady(true);
        iframeRef.current?.contentWindow?.postMessage({ type: "load", config }, "*");
        iframeRef.current?.contentWindow?.postMessage({ type: "setDebug", on: true }, "*");
      }
      if (d.type === "debug") setHud(d);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [open, config]);

  const switchCam = (mode) => { setCam(mode); send({ type: "setCamera", mode }); };
  const restart = () => { send({ type: "restart" }); setPaused(false); };
  const togglePause = () => { const p = !paused; setPaused(p); send({ type: "setPaused", paused: p }); };
  const fullscreen = () => { wrapRef.current?.requestFullscreen?.(); };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-[#05060a]/97 backdrop-blur-sm flex flex-col" data-testid="test-play-overlay">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 lg:px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <FlaskConical className="w-5 h-5 text-[#3b9eff]" />
          <div className="min-w-0">
            <div className="font-bold text-white truncate flex items-center gap-2">
              {config?.player?.emoji} {config?.title || "Test Play"}
              <Badge variant="secondary" className="text-[10px]">Private playtest</Badge>
            </div>
            <div className="text-[11px] text-white/50">Nothing here is saved or published — just play around.</div>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="text-white/70 hover:text-white shrink-0" data-testid="test-play-close">
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Stage */}
      <div className="flex-1 min-h-0 grid lg:grid-cols-[1fr_280px] gap-4 p-4 lg:p-6 overflow-hidden">
        <div ref={wrapRef} className="relative rounded-2xl overflow-hidden border-2 border-[#3b9eff]/30 bg-black min-h-0">
          <iframe
            ref={iframeRef}
            title="GameView Test Play"
            src={runnerSrc}
            key={runnerKey}
            sandbox="allow-scripts allow-same-origin"
            allow="fullscreen; gamepad; autoplay"
            allowFullScreen
            className="w-full h-full block"
            data-testid="test-play-iframe"
          />
          {/* Debug HUD */}
          {is3d && hud && (
            <div className="absolute top-3 left-3 rounded-xl bg-black/70 border border-white/10 px-3 py-2 text-[11px] font-mono text-emerald-300 space-y-0.5 pointer-events-none" data-testid="test-play-hud">
              <div className="flex items-center gap-1.5"><Gauge className="w-3 h-3" /> {hud.fps} FPS</div>
              <div className="flex items-center gap-1.5 text-sky-300"><MapPin className="w-3 h-3" /> {hud.x}, {hud.y}, {hud.z}</div>
              <div className="text-white/50">cam: {hud.cam} · score: {hud.score}</div>
            </div>
          )}
          {!ready && (
            <div className="absolute inset-0 grid place-items-center bg-black/80 text-white">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full border-2 border-white/20 border-t-[#3b9eff] animate-spin" />
                <p className="text-sm text-white/70">Loading engine…</p>
              </div>
            </div>
          )}
        </div>

        {/* Controls sidebar */}
        <div className="space-y-4 overflow-y-auto">
          {is3d && (
            <Panel icon={Video} title="Camera">
              <div className="grid grid-cols-3 gap-1.5">
                {CAMERAS.map(([mode, label, Icon]) => (
                  <button key={mode} onClick={() => switchCam(mode)} data-testid={`cam-${mode}`}
                    className={`flex flex-col items-center gap-1 rounded-xl py-2.5 text-[10px] font-semibold transition-colors ${cam === mode ? "bg-[#1f6feb] text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>
            </Panel>
          )}

          <Panel icon={Gamepad2} title="Playback">
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" size="sm" onClick={restart} className="rounded-full" data-testid="test-play-restart">
                <RotateCcw className="w-4 h-4" /> Restart
              </Button>
              {is3d ? (
                <Button variant="secondary" size="sm" onClick={togglePause} className="rounded-full" data-testid="test-play-pause">
                  {paused ? <><Play className="w-4 h-4" /> Resume</> : <><Pause className="w-4 h-4" /> Pause</>}
                </Button>
              ) : (
                <Button variant="secondary" size="sm" onClick={fullscreen} className="rounded-full" data-testid="test-play-fullscreen">
                  <Maximize2 className="w-4 h-4" /> Fullscreen
                </Button>
              )}
              {is3d && (
                <Button variant="secondary" size="sm" onClick={fullscreen} className="rounded-full col-span-2" data-testid="test-play-fullscreen-3d">
                  <Maximize2 className="w-4 h-4" /> Fullscreen
                </Button>
              )}
            </div>
          </Panel>

          <Panel icon={Keyboard} title="Controls">
            <ul className="text-[12px] text-white/70 space-y-1.5">
              <li><Kbd>W A S D</Kbd> / <Kbd>Arrows</Kbd> — move</li>
              <li><Kbd>Space</Kbd> — jump / action</li>
              <li className="text-white/50">On touch devices, an on-screen joystick + action button appear automatically.</li>
            </ul>
          </Panel>

          {config?.instructions && (
            <Panel icon={FlaskConical} title="Objective">
              <p className="text-[12px] text-white/70">{config.instructions}</p>
            </Panel>
          )}

          {!is3d && (
            <p className="text-[11px] text-white/40 px-1">Camera tools and the live debug HUD are available for 3D games.</p>
          )}
        </div>
      </div>
    </div>
  );
}

const Panel = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3.5">
    <div className="text-xs font-bold text-white mb-2.5 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-[#3b9eff]" /> {title}</div>
    {children}
  </div>
);
const Kbd = ({ children }) => (
  <kbd className="inline-block px-1.5 py-0.5 rounded bg-white/10 border border-white/15 text-[10px] font-mono text-white/90">{children}</kbd>
);
